import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface CrmConnectionStatus {
  connectionId: string;
  provider: string;
  category: string;
  status: string;
  createdAt: string | null;
  lastSyncAt: string | null;
  lastSyncResult: string | null;
  dealsSynced: number;
  advisersSynced: number;
  adviserContactIds: string[];
  // Phase E observability — populated from `crm_connections.lastSyncErrors`
  // (written by syncCrmData.updateConnectionAfterSync). The
  // CrmSyncStatusCard renders the last 3 entries.
  lastSyncErrors: string[];
}

interface GetStatusResponse {
  connections: CrmConnectionStatus[];
}

interface ConnectResponse {
  success: boolean;
  connectionId: string;
  message: string;
}

interface DisconnectResponse {
  success: boolean;
  message: string;
}

interface TriggerSyncResponse {
  success: boolean;
  outcomes: Array<{
    connectionId: string;
    provider: string;
    status: 'success' | 'partial' | 'failed';
    advisersSynced: number;
    dealsSynced: number;
    alertsCreated: number;
    durationMs: number;
    errors: string[];
  }>;
}

export interface UseCrmConnectionState {
  connections: CrmConnectionStatus[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  connect: (args: {
    clientId: string;
    clientSecret: string;
    authUrl: string;
    adviserContactIds?: string[];
    scope?: string;
  }) => Promise<ConnectResponse>;
  disconnect: (connectionId: string) => Promise<DisconnectResponse>;
  triggerSync: (connectionId?: string) => Promise<TriggerSyncResponse>;
}

export function useCrmConnection(companyId: string | undefined): UseCrmConnectionState {
  const [connections, setConnections] = useState<CrmConnectionStatus[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    const fn = httpsCallable<{ companyId: string }, GetStatusResponse>(
      functions,
      'getCrmConnectionStatus'
    );
    setLoading(true);
    setError(null);
    fn({ companyId })
      .then((r) => setConnections(r.data.connections))
      .catch((err) => {
        console.error('[useCrmConnection] fetch failed', err);
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  }, [companyId, refreshKey]);

  const connect = useCallback(
    async (args: {
      clientId: string;
      clientSecret: string;
      authUrl: string;
      adviserContactIds?: string[];
      scope?: string;
    }) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          companyId: string;
          provider: 'mycrm';
          clientId: string;
          clientSecret: string;
          authUrl: string;
          adviserContactIds?: string[];
          scope?: string;
        },
        ConnectResponse
      >(functions, 'connectMyCrmCredentials');
      const result = await fn({ companyId, provider: 'mycrm', ...args });
      setRefreshKey((k) => k + 1);
      return result.data;
    },
    [companyId]
  );

  const disconnect = useCallback(
    async (connectionId: string) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        { companyId: string; connectionId: string },
        DisconnectResponse
      >(functions, 'disconnectMyCrm');
      const result = await fn({ companyId, connectionId });
      setRefreshKey((k) => k + 1);
      return result.data;
    },
    [companyId]
  );

  const triggerSync = useCallback(
    async (connectionId?: string) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        { companyId: string; connectionId?: string },
        TriggerSyncResponse
      >(functions, 'triggerCrmSync');
      const result = await fn({ companyId, connectionId });
      setRefreshKey((k) => k + 1);
      return result.data;
    },
    [companyId]
  );

  return { connections, loading, error, refresh, connect, disconnect, triggerSync };
}
